"use client";

import ShareInvite from "@/containers/connect/ShareInvite";
import { SubscriptionStatus } from "@/lib/types";
import { Koppeling } from "@/lib/parent_student_types";

interface SubscriptionItemProps {
  koppeling: Koppeling;
}

export default function SubscriptionItem({ koppeling }: SubscriptionItemProps) {
  const isPendingInvite = !koppeling.student_email;

  const getStatusMessage = (status: SubscriptionStatus | undefined) => {
    switch (status) {
      case SubscriptionStatus.active:
        return "Kan nu wiskunde leren met alle uitleg van WisWiz";
      case SubscriptionStatus.paused:
        return "Abonnement is gepauzeerd; toegang blijft actief";
      case SubscriptionStatus.canceled:
        return "Dit abonnement is opgezegd";
      case SubscriptionStatus.past_due:
        return "Er is een betalingsprobleem met dit abonnement";
      case SubscriptionStatus.pending:
        return "Abonnement is in behandeling";
      default:
        return "Abonnement status onbekend";
    }
  };

  return (
    <div
      className={`rounded-2xl border p-4 ${
        isPendingInvite
          ? "border-mathyellow/60 bg-white shadow-sm"
          : "border-gray-200 bg-white/90"
      }`}
    >
      {koppeling.student_email ? (
        // Accepted student slot
        <div className="flex flex-col gap-4">
          {/* Student info */}
          <div>
            {koppeling.student_name && (
              <p className="font-semibold text-gray-900">
                {koppeling.student_name}
              </p>
            )}
            <p
              className={`text-gray-900 ${koppeling.student_name ? "text-sm" : "font-semibold"}`}
            >
              {koppeling.student_email}
            </p>
          </div>

          {/* Status message */}
          <div
            className={`rounded-lg p-3 ${koppeling.subscription_status === SubscriptionStatus.canceled || koppeling.subscription_status === SubscriptionStatus.past_due ? "bg-red-50" : "bg-green-50"}`}
          >
            <p className="text-sm text-gray-700 flex items-center gap-2">
              {koppeling.subscription_status === SubscriptionStatus.canceled ||
              koppeling.subscription_status === SubscriptionStatus.past_due ? (
                <svg
                  className="w-4 h-4 text-red-600 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  className="w-4 h-4 text-green-600 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              <span>{getStatusMessage(koppeling.subscription_status)}</span>
            </p>
          </div>
        </div>
      ) : (
        // Pending invite slot
        <div className="flex flex-col gap-4">
          <p className="px-2 py-1 text-center text-sm font-medium text-gray-600">
            Deel via WhatsApp of kopieer de link
          </p>

          {/* Share invite */}
          {koppeling.invite_token && (
            <ShareInvite
              invite_token={koppeling.invite_token}
              expiresAt={koppeling.expiresAt}
              inviterRole="parent"
            />
          )}
        </div>
      )}
    </div>
  );
}
